import React, {Fragment, ReactNode, RefAttributes, useEffect, useState} from 'react';
import {RowCollapse, State} from './components/row-collapse';
import {
    GridColumnProps,
    GridFlexProps, GridGroupByProps, GridRef, GridProps,
    GridRowModel
} from './types';
import styles from './grid.module.scss'
import classNames from 'classnames';
import {Lx} from '../../../i18n/consts';

import _ from 'lodash';
import GridRow from './grid-row';
import { CollapseButton } from '../collapse-button';
import {Tooltip} from '@mui/material';

const TimiDataGrid = (props: GridProps, ref: React.Ref<GridRef>) => {
    const {columns, groupBy, intl: externalIntl} = props;
    const [init, setInit] = useState(false);
    const [collapseRows, setCollapseRows] = useState<Array<State>>([])
    const intl = externalIntl
    const lazyDataLoad = props.editDataLoadingMode !== undefined && props.editDataLoadingMode === 'server';
    const gridRef = React.useRef<HTMLDivElement>(null);
    const rows = React.useMemo(() => props.rows, [props.rows]);
    const initialExpandedKeys = React.useMemo(
        () => new Set((props.initialExpandedRowKeys ?? []).map((rowKey) => String(rowKey))),
        [props.initialExpandedRowKeys]
    );
    const topLevelGroupKeys = React.useMemo(() => {
        if (!groupBy || !groupBy.rowGroupingModel.length) {
            return [] as string[];
        }

        return _.chain(rows).groupBy(groupBy.rowGroupingModel[0]).keys().filter((key) => key !== '').value();
    }, [rows, groupBy]);
    const shouldAutoExpandSingleGroup = !!props.autoExpandSingleGroup && topLevelGroupKeys.length === 1;
    // Derived, never mirrored in state: a stale copy is what made the header offer "expand" while
    // a group was already open.
    const isAnyRowExpand = React.useMemo(
        () => collapseRows.some((row) => row.open && row.isAbleToOpen),
        [collapseRows]
    );

    React.useImperativeHandle(ref, () => ({
        element: gridRef.current,
        collapseRows: refHandleExpand,
        getExpandedRowKeys: () => collapseRows
            .filter((row) => row.open && row.isAbleToOpen)
            .map((row) => String(row.rowKey))
    }), [init, collapseRows, rows, isAnyRowExpand]);

    const refHandleExpand = () => {
        if (init) {
            handleExpand();
        }
    }

    const handleRowStateChange = (state: State) => {
        setCollapseRows((prevState) => {
            const index = prevState.findIndex((item) => item.rowKey === state.rowKey);
            const nextState = prevState.slice();

            if (index > -1) {
                nextState[index] = state;
            } else {
                nextState.push(state);
            }

            return nextState;
        });
    };

    const handleExpand = () => {
        const shouldExpand = !isAnyRowExpand;

        collapseRows
            .filter((row) => row.isAbleToOpen)
            .forEach((row) => (shouldExpand ? row.forceExpand() : row.forceCollapse()));

        setCollapseRows((prevState) => prevState.map((row) => row.isAbleToOpen
            ? { ...row, open: shouldExpand }
            : row));
    }

    const hasVisibleColumns = (gridColumns: GridColumnProps[]) => {
        for (let index = 0; index < gridColumns.length; index++) {
            const column = gridColumns[index];
            if ( column.visible === undefined || column.visible) {
                return true;
            }
        }

        return false;
    };

    const generateTemplateColumns = (columns: GridColumnProps[]): React.CSSProperties => {
        const values: string[] = [];

        columns.filter((x) => x.visible === undefined || x.visible).forEach((column: GridColumnProps) => {
            const width = column.width ? `${column.width}px` : 'auto';
            const flexed = column.flex !== undefined ? typeof column.flex === 'boolean' ? 'minmax(0, 1fr)'
                : `minmax(0, ${(column.flex as GridFlexProps).grow}fr)` : null;

            if (flexed !== null) {
                values.push(flexed);
            } else {
                values.push(width);
            }
        })

        return { gridTemplateColumns: `${values.join(' ')}` };
    }

    const recursivelyGenerateGroupRows = (groupByProps: GridGroupByProps, groupFields: string[], rows: GridRowModel[], startIndex: number): ReactNode[] => {
        if (startIndex > 0 && groupFields.length < startIndex + 1) {
            return rows.map((r, index) => {
                return (<GridRow key={index} index={index} columns={columns} row={r} intl={intl}
                    onRowEditStop={props.onRowEditEnd} onRowClick={props.onRowClick} lazy={lazyDataLoad}
                    noHeader={props.hideHeader ?? false} />)
            })
        }

        const field = groupFields[startIndex];
        return _.chain(rows).groupBy(field).map((value, key) => {
            if (key === '') {
                return (<Fragment key={'empty-key'} />);
            }
            return (value.length > 1 || startIndex < 1
                ? startIndex < 1 
                    ? <RowCollapse nested={startIndex} key={`collapse-${key}`} rowKey={key}
                        rowHeader={groupByProps.groupByRenderer(field, key, value, intl, startIndex + 1)}
                        onStateChange={(startIndex < 1 ? handleRowStateChange : () => void (0))}
                        initialOpen={initialExpandedKeys.has(key) || shouldAutoExpandSingleGroup}
                        contentProps={{
                            style: generateTemplateColumns(columns)
                        }} 
                        showButton={groupByProps.isGroupHasValues ? groupByProps.isGroupHasValues(value) : hasVisibleColumns(columns)}>
                        {recursivelyGenerateGroupRows(groupByProps, groupFields, value, startIndex + 1)}
                    </RowCollapse> 
                    : <Fragment key={key}>
                        {recursivelyGenerateGroupRows(groupByProps, groupFields, value, startIndex + 1)}
                        {
                            value.length <= startIndex + 1 && <div id={`separator-${field}`} key={`separator-${field}`}
                                className={styles.row + ' ' + styles.separator}></div>
                        }
                    </Fragment>
                : <Fragment key={key}>
                    <GridRow key={value[0].id} index={value[0].id} columns={columns} row={value[0]} intl={intl}
                        onRowEditStop={props.onRowEditEnd} onRowClick={props.onRowClick} lazy={lazyDataLoad}
                        noHeader={props.hideHeader ?? false}/>
                    <div id={`separator-${field}`} key={`separator-${field}`}
                        className={styles.row + ' ' + styles.separator}></div>
                </Fragment>)
        }).value()
    }

    // Groups that disappear with new rows unmount without reporting, so drop them here instead of
    // letting their last known "open" keep the header in the expanded state.
    useEffect(() => {
        const existingKeys = new Set(topLevelGroupKeys.map((key) => String(key)));

        setCollapseRows((prevState) => {
            const nextState = prevState.filter((row) => existingKeys.has(String(row.rowKey)));

            return nextState.length === prevState.length ? prevState : nextState;
        });
    }, [topLevelGroupKeys]);

    useEffect(() => {
        props.onAnyRowExpand?.(isAnyRowExpand);
    }, [isAnyRowExpand]);

    useEffect(() => {
        if (!init) {
            setInit(true);
        }
    }, [])

    const bodyStyle: React.CSSProperties | undefined = props.disableBodyScroll
        ? { height: 'auto' }
        : undefined;
    const headerStyle: React.CSSProperties = generateTemplateColumns(columns);
    const getHeaderCellStyle = (header: GridColumnProps): React.CSSProperties | undefined =>
        header.flex
            ? undefined
            : { width: header.width ? `${header.width}px` : '150px' };

    return (<div className={classNames(styles.container, props.className)} ref={gridRef}>
        {/*{*/}
        {/*    <div className={styles.topBorder}/>*/}
        {/*}*/}
        {
            !props.hideHeader && <div className={classNames(styles.header, props.headerClassName)} style={headerStyle}>
                {
                    columns.filter((c) => (c.visible === undefined || c.visible) && !c.noHeader).map((header, index) => {
                        const content = header.hideHeaderContent
                            ? ''
                            : header.localizationKey
                                ? intl.formatMessage({ id: header.localizationKey })
                                : header.field;
                        const headerContent = header.headerTooltipKey && content
                            ? (
                                <Tooltip title={intl.formatMessage({id: header.headerTooltipKey})}>
                                    <span>{content}</span>
                                </Tooltip>
                            )
                            : content;

                        return header.isHeaderCollapseButton && header.isHeaderCollapseButton ?
                            <div key={`column_${index}`} className={classNames({
                                [styles.headerCell]: true,
                                [styles.flex]: header.flex ? header.flex : false
                            })} style={getHeaderCellStyle(header)}>
                                <CollapseButton 
                                    displayText={isAnyRowExpand ? 
                                        intl.formatMessage({ id: Lx.General.COLLAPSE_ALL }) : 
                                        intl.formatMessage({ id: Lx.General.EXPAND_ALL })}
                                    isExpanded={isAnyRowExpand}
                                    onExpandClick={() => handleExpand()} />
                            </div>
                            : <div key={`column_${index}`} className={classNames({
                                [styles.headerCell]: true,
                                [styles.flex]: header.flex ? header.flex : false
                            })} style={getHeaderCellStyle(header)}>
                                {headerContent}
                            </div>;
                    })
                }
            </div>
        }
        <div className={classNames(styles.body, props.bodyClassName)} style={bodyStyle}>
            {
                !groupBy ? rows.map((r, index) => (
                    <GridRow index={index} key={index} columns={columns} row={r} intl={intl} onRowEditStop={props.onRowEditEnd}
                        onRowClick={props.onRowClick} lazy={lazyDataLoad} noHeader={props.hideHeader ?? false} />))
                    : recursivelyGenerateGroupRows(groupBy, groupBy.rowGroupingModel, rows, 0)
            }
        </div>
    </div>)
}

export default React.forwardRef(TimiDataGrid) as (props: GridProps & RefAttributes<GridRef>) => React.JSX.Element;
